import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { FaSearch, FaEdit, FaCheck, FaTimes, FaExchangeAlt, FaPlus } from 'react-icons/fa';

export default function AdminComparator() {
  const { i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, withSpecs: 0, complete: 0 });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/comparator/admin/all-specs`);
      setProducts(response.data);
      
      // Calculate stats
      const total = response.data.length;
      const withSpecs = response.data.filter(p => p.has_specs).length;
      const complete = response.data.filter(p => p.specs_complete).length;
      setStats({ total, withSpecs, complete });
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p => 
    p.name_fr?.toLowerCase().includes(search.toLowerCase()) ||
    p.brand?.toLowerCase().includes(search.toLowerCase()) ||
    p.model?.toLowerCase().includes(search.toLowerCase())
  );

  const progressPercent = stats.total > 0 ? Math.round((stats.complete / stats.total) * 100) : 0;

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2540] flex items-center gap-3">
              <FaExchangeAlt className="text-[#635BFF]" />
              {i18n.language === 'fr' ? 'Gestion Comparateur' : 'Comparator Management'}
            </h1>
            <p className="text-gray-500">
              {i18n.language === 'fr' 
                ? 'Gérez les spécifications techniques des produits'
                : 'Manage technical specifications for products'}
            </p>
          </div>
          <Link to="/comparateur">
            <Button variant="outline">
              {i18n.language === 'fr' ? 'Voir le comparateur' : 'View comparator'}
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-[#0A2540]">{stats.total}</div>
              <div className="text-sm text-gray-500">
                {i18n.language === 'fr' ? 'Produits total' : 'Total products'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-yellow-600">{stats.total - stats.withSpecs}</div>
              <div className="text-sm text-gray-500">
                {i18n.language === 'fr' ? 'Sans spécifications' : 'Without specs'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-blue-600">{stats.withSpecs - stats.complete}</div>
              <div className="text-sm text-gray-500">
                {i18n.language === 'fr' ? 'Specs partielles' : 'Partial specs'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-green-600">{stats.complete}</div>
              <div className="text-sm text-gray-500">
                {i18n.language === 'fr' ? 'Specs complètes' : 'Complete specs'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {i18n.language === 'fr' ? 'Progression' : 'Progress'}
              </span>
              <span className="text-sm text-gray-500">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
            <p className="text-xs text-gray-400 mt-2">
              {stats.complete} / {stats.total} {i18n.language === 'fr' ? 'produits avec specs complètes' : 'products with complete specs'}
            </p>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative mb-6">
          <Input
            placeholder={i18n.language === 'fr' ? 'Rechercher un produit...' : 'Search product...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>

        {/* Products Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#635BFF]"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-gray-600">
                        {i18n.language === 'fr' ? 'Produit' : 'Product'}
                      </th>
                      <th className="text-left p-4 font-medium text-gray-600">
                        {i18n.language === 'fr' ? 'Marque / Modèle' : 'Brand / Model'}
                      </th>
                      <th className="text-center p-4 font-medium text-gray-600">
                        {i18n.language === 'fr' ? 'Statut Specs' : 'Specs Status'}
                      </th>
                      <th className="text-center p-4 font-medium text-gray-600">
                        {i18n.language === 'fr' ? 'Dernière MAJ' : 'Last Update'}
                      </th>
                      <th className="text-center p-4 font-medium text-gray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(product => (
                      <tr key={product.product_id} className="border-b hover:bg-gray-50">
                        <td className="p-4">
                          <span className="font-medium text-[#0A2540]">{product.name_fr}</span>
                        </td>
                        <td className="p-4 text-gray-600">
                          {product.brand || '-'} {product.model || ''}
                        </td>
                        <td className="p-4 text-center">
                          {product.specs_complete ? (
                            <Badge className="bg-green-500">
                              <FaCheck className="mr-1" />
                              {i18n.language === 'fr' ? 'Complet' : 'Complete'}
                            </Badge>
                          ) : product.has_specs ? (
                            <Badge className="bg-yellow-500">
                              {i18n.language === 'fr' ? 'Partiel' : 'Partial'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">
                              <FaTimes className="mr-1" />
                              {i18n.language === 'fr' ? 'Aucun' : 'None'}
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 text-center text-sm text-gray-500">
                          {product.updated_at 
                            ? new Date(product.updated_at).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="p-4 text-center">
                          <Button 
                            size="sm"
                            onClick={() => navigate(`/admin/comparator/${product.product_id}`)}
                            className={product.has_specs ? 'bg-blue-500 hover:bg-blue-600' : 'bg-[#635BFF] hover:bg-[#5248d9]'}
                          >
                            {product.has_specs ? (
                              <>
                                <FaEdit className="mr-1" />
                                {i18n.language === 'fr' ? 'Modifier' : 'Edit'}
                              </>
                            ) : (
                              <>
                                <FaPlus className="mr-1" />
                                {i18n.language === 'fr' ? 'Ajouter' : 'Add'}
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredProducts.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    {i18n.language === 'fr' ? 'Aucun produit trouvé' : 'No products found'}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
